// Report math over llc_trial_balance rows. Pure functions: no React, no
// Supabase. Every amount is integer cents. A debit-normal account's balance is
// debits − credits; a credit-normal account's is credits − debits.
import { MEMBERS, TAX_LINE_LABELS } from './types';
import type { Member, MemberCapital, TaxLine, TrialBalanceRow } from './types';

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export type SectionRow = { account_id: string; code: string; name: string; subtype: string; amount_cents: number };
export type Section = { rows: SectionRow[]; total_cents: number };

export type ProfitLoss = {
  income: Section;
  cogs: Section;
  expenses: Section;
  gross_profit_cents: number;
  net_income_cents: number;
};

export type BalanceSheet = {
  assets: Section;
  liabilities: Section;
  equity: Section;
  net_income_to_date_cents: number;
  total_assets_cents: number;
  total_liabilities_and_equity_cents: number;
  balanced: boolean;
  /** Signed subtotal per `type/subtype`: assets debit-positive, liabilities and equity credit-positive. */
  by_subtype: Record<string, number>;
  cash_cents: number;
};

export type CashFlowLine = { label: string; amount_cents: number };
export type CashFlowSection = { rows: CashFlowLine[]; total_cents: number };
export type CashFlow = {
  net_income_cents: number;
  operating: CashFlowSection;
  investing: CashFlowSection;
  financing: CashFlowSection;
  net_change_cents: number;
  beginning_cash_cents: number;
  ending_cash_cents: number;
  ties: boolean;
};

export type TrialBalanceLine = { account_id: string; code: string; name: string; debit_cents: number; credit_cents: number };
export type TrialBalance = { rows: TrialBalanceLine[]; total_debit_cents: number; total_credit_cents: number; balanced: boolean };

export type BalanceSheetSummary = {
  total_assets_cents: number;
  total_liabilities_cents: number;
  total_equity_cents: number;
  cash_cents: number;
  net_income_to_date_cents: number;
};

export type Form1065 = {
  page1: {
    lines: { tax_line: TaxLine; label: string; amount_cents: number }[];
    total_income_cents: number;
    total_deductions_cents: number;
    ordinary_income_cents: number;
  };
  schedule_k: { ordinary_income_cents: number; distributions_cents: number };
  k1: Record<Member, {
    pct: number;
    ordinary_income_cents: number;
    distributions_cents: number;
    capital: MemberCapital['members'][Member];
  }>;
  schedule_l: { begin: BalanceSheetSummary; end: BalanceSheetSummary };
  /** Income, cost or expense accounts with activity but no tax line. They are missing from page 1. */
  untagged: SectionRow[];
};

// ── balances ─────────────────────────────────────────────────────────────────

/** Debits − credits for the row, opening included when asked. */
function debitNet(row: TrialBalanceRow, includeOpening: boolean): number {
  const d = num(row.debit_cents) + (includeOpening ? num(row.opening_debit_cents) : 0);
  const c = num(row.credit_cents) + (includeOpening ? num(row.opening_credit_cents) : 0);
  return d - c;
}

/** Signed balance by the account's normal side. */
export function balanceOf(row: TrialBalanceRow, { includeOpening = false }: { includeOpening?: boolean } = {}): number {
  const net = debitNet(row, includeOpening);
  return row.normal_balance === 'credit' ? -net : net;
}

function sortRows<T extends { display_order: number; code: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (num(a.display_order) - num(b.display_order)) || a.code.localeCompare(b.code));
}

function section(rows: TrialBalanceRow[], amount: (r: TrialBalanceRow) => number, keepZero = false): Section {
  const out: SectionRow[] = [];
  for (const r of sortRows(rows)) {
    const a = amount(r);
    if (a === 0 && !keepZero) continue;
    out.push({ account_id: r.account_id, code: r.code, name: r.name, subtype: r.subtype, amount_cents: a });
  }
  return { rows: out, total_cents: out.reduce((s, r) => s + r.amount_cents, 0) };
}

// ── profit and loss ──────────────────────────────────────────────────────────

/** Income positive when credit (contra-income like Refunds shows negative); cost and expense positive. */
export function buildProfitLoss(tb: TrialBalanceRow[], { includeOpening = false }: { includeOpening?: boolean } = {}): ProfitLoss {
  const income = section(tb.filter((r) => r.type === 'income'), (r) => -debitNet(r, includeOpening));
  const cogs = section(tb.filter((r) => r.type === 'cogs'), (r) => debitNet(r, includeOpening));
  const expenses = section(tb.filter((r) => r.type === 'expense'), (r) => debitNet(r, includeOpening));
  const gross_profit_cents = income.total_cents - cogs.total_cents;
  return { income, cogs, expenses, gross_profit_cents, net_income_cents: gross_profit_cents - expenses.total_cents };
}

// ── balance sheet ────────────────────────────────────────────────────────────

const EQUITY_ORDER = ['member_capital', 'member_distributions', 'opening_equity'];
export const NET_INCOME_ROW_ID = 'net_income_to_date';

/**
 * Give this `trialBalance(FAR_PAST, asOf)` so opening is zero and the period
 * holds everything. Equity ends with a synthetic "Net income to date" row so
 * the sheet balances.
 */
export function buildBalanceSheet(tb: TrialBalanceRow[]): BalanceSheet {
  const all = true;
  const assets = section(tb.filter((r) => r.type === 'asset'), (r) => debitNet(r, all));
  const liabilities = section(tb.filter((r) => r.type === 'liability'), (r) => -debitNet(r, all));
  const equityRows = [...tb.filter((r) => r.type === 'equity')].sort((a, b) => {
    const ia = EQUITY_ORDER.indexOf(a.subtype); const ib = EQUITY_ORDER.indexOf(b.subtype);
    const ra = ia < 0 ? EQUITY_ORDER.length : ia; const rb = ib < 0 ? EQUITY_ORDER.length : ib;
    return (ra - rb) || (num(a.display_order) - num(b.display_order)) || a.code.localeCompare(b.code);
  });
  const equity: Section = { rows: [], total_cents: 0 };
  for (const r of equityRows) {
    const a = -debitNet(r, all);
    if (a === 0) continue;
    equity.rows.push({ account_id: r.account_id, code: r.code, name: r.name, subtype: r.subtype, amount_cents: a });
  }
  const pl = buildProfitLoss(tb, { includeOpening: true });
  const net_income_to_date_cents = pl.net_income_cents;
  equity.rows.push({ account_id: NET_INCOME_ROW_ID, code: '', name: 'Net income to date', subtype: 'net_income', amount_cents: net_income_to_date_cents });
  equity.total_cents = equity.rows.reduce((s, r) => s + r.amount_cents, 0);

  const by_subtype: Record<string, number> = {};
  for (const r of tb) {
    if (r.type !== 'asset' && r.type !== 'liability' && r.type !== 'equity') continue;
    const key = `${r.type}/${r.subtype}`;
    const signed = r.type === 'asset' ? debitNet(r, all) : -debitNet(r, all);
    by_subtype[key] = (by_subtype[key] ?? 0) + signed;
  }
  const cash_cents = (by_subtype['asset/bank'] ?? 0) + (by_subtype['asset/vessel'] ?? 0);
  const total_assets_cents = assets.total_cents;
  const total_liabilities_and_equity_cents = liabilities.total_cents + equity.total_cents;
  return {
    assets, liabilities, equity, net_income_to_date_cents, total_assets_cents, total_liabilities_and_equity_cents,
    balanced: total_assets_cents === total_liabilities_and_equity_cents, by_subtype, cash_cents,
  };
}

export function summarizeBalanceSheet(bs: BalanceSheet | null): BalanceSheetSummary {
  if (!bs) return { total_assets_cents: 0, total_liabilities_cents: 0, total_equity_cents: 0, cash_cents: 0, net_income_to_date_cents: 0 };
  return {
    total_assets_cents: bs.total_assets_cents,
    total_liabilities_cents: bs.liabilities.total_cents,
    total_equity_cents: bs.equity.total_cents,
    cash_cents: bs.cash_cents,
    net_income_to_date_cents: bs.net_income_to_date_cents,
  };
}

// ── cash flow (indirect) ─────────────────────────────────────────────────────

const CASH_SUBTYPES = ['bank', 'vessel'];
const OPERATING_ASSETS = ['accounts_receivable', 'prepaid'];
const OPERATING_LIABILITIES = ['accounts_payable', 'related_party', 'due_to_member', 'credit_card'];
const INVESTING_ASSETS = ['fixed_asset', 'accumulated_depreciation'];
const FINANCING_EQUITY = ['member_capital', 'member_distributions', 'opening_equity'];

const LABELS: Record<string, string> = {
  accounts_receivable: 'Change in amounts due from Apple and others',
  prepaid: 'Change in prepaid expenses',
  accounts_payable: 'Change in accounts payable',
  related_party: 'Change in amounts due to related parties',
  due_to_member: 'Change in amounts due to members',
  credit_card: 'Change in credit card balance',
  member_capital: 'Member contributions',
  member_distributions: 'Member distributions',
  opening_equity: 'Opening balance equity',
};

/**
 * Start with net income, add back depreciation, adjust for working capital,
 * then investing and financing, ending with the change in cash. `ties` is
 * true when beginning cash + net change equals ending cash. `pl` must cover
 * exactly the days between the two balance sheets.
 */
export function buildCashFlow(pl: ProfitLoss, bsStart: BalanceSheet, bsEnd: BalanceSheet): CashFlow {
  const delta = (key: string) => (bsEnd.by_subtype[key] ?? 0) - (bsStart.by_subtype[key] ?? 0);
  const keys = new Set([...Object.keys(bsStart.by_subtype), ...Object.keys(bsEnd.by_subtype)]);
  const subtypesOf = (type: string) => [...keys].filter((k) => k.startsWith(`${type}/`)).map((k) => k.slice(type.length + 1));

  const depreciation = pl.expenses.rows.filter((r) => r.subtype === 'depreciation').reduce((s, r) => s + r.amount_cents, 0);

  const operating: CashFlowLine[] = [
    { label: 'Net income', amount_cents: pl.net_income_cents },
    { label: 'Depreciation', amount_cents: depreciation },
  ];
  for (const s of OPERATING_ASSETS) operating.push({ label: LABELS[s], amount_cents: -delta(`asset/${s}`) });
  for (const s of OPERATING_LIABILITIES) operating.push({ label: LABELS[s], amount_cents: delta(`liability/${s}`) });
  const otherAssets = subtypesOf('asset').filter((s) => !CASH_SUBTYPES.includes(s) && !OPERATING_ASSETS.includes(s) && !INVESTING_ASSETS.includes(s))
    .reduce((sum, s) => sum - delta(`asset/${s}`), 0);
  if (otherAssets !== 0) operating.push({ label: 'Change in other assets', amount_cents: otherAssets });
  const otherLiabilities = subtypesOf('liability').filter((s) => !OPERATING_LIABILITIES.includes(s))
    .reduce((sum, s) => sum + delta(`liability/${s}`), 0);
  if (otherLiabilities !== 0) operating.push({ label: 'Change in other liabilities', amount_cents: otherLiabilities });

  // Gross cost moves with purchases and disposals; accumulated depreciation moves
  // with depreciation (already added back) and with what a disposal releases.
  const fixedAssets = -delta('asset/fixed_asset');
  const released = -delta('asset/accumulated_depreciation') - depreciation;
  const investing: CashFlowLine[] = [{ label: 'Fixed asset purchases and disposals, net', amount_cents: fixedAssets + released }];

  const financing: CashFlowLine[] = [
    { label: LABELS.member_capital, amount_cents: delta('equity/member_capital') },
    { label: LABELS.member_distributions, amount_cents: delta('equity/member_distributions') },
  ];
  const opening = delta('equity/opening_equity');
  if (opening !== 0) financing.push({ label: LABELS.opening_equity, amount_cents: opening });
  const otherEquity = subtypesOf('equity').filter((s) => !FINANCING_EQUITY.includes(s))
    .reduce((sum, s) => sum + delta(`equity/${s}`), 0);
  if (otherEquity !== 0) financing.push({ label: 'Other equity changes', amount_cents: otherEquity });

  const total = (rows: CashFlowLine[]) => rows.reduce((s, r) => s + r.amount_cents, 0);
  const op = { rows: operating, total_cents: total(operating) };
  const inv = { rows: investing, total_cents: total(investing) };
  const fin = { rows: financing, total_cents: total(financing) };
  const net_change_cents = op.total_cents + inv.total_cents + fin.total_cents;
  const beginning_cash_cents = bsStart.cash_cents;
  const ending_cash_cents = bsEnd.cash_cents;
  return {
    net_income_cents: pl.net_income_cents,
    operating: op, investing: inv, financing: fin,
    net_change_cents, beginning_cash_cents, ending_cash_cents,
    ties: beginning_cash_cents + net_change_cents === ending_cash_cents,
  };
}

// ── trial balance ────────────────────────────────────────────────────────────

/** Each account netted to one side. Zero-balance accounts are left out. */
export function buildTrialBalance(tb: TrialBalanceRow[], { includeOpening = false }: { includeOpening?: boolean } = {}): TrialBalance {
  const rows: TrialBalanceLine[] = [];
  for (const r of sortRows(tb)) {
    const net = debitNet(r, includeOpening);
    if (net === 0) continue;
    rows.push({ account_id: r.account_id, code: r.code, name: r.name, debit_cents: net > 0 ? net : 0, credit_cents: net < 0 ? -net : 0 });
  }
  const total_debit_cents = rows.reduce((s, r) => s + r.debit_cents, 0);
  const total_credit_cents = rows.reduce((s, r) => s + r.credit_cents, 0);
  return { rows, total_debit_cents, total_credit_cents, balanced: total_debit_cents === total_credit_cents };
}

// ── Form 1065 ────────────────────────────────────────────────────────────────

export const TAX_LINE_ORDER: TaxLine[] = [
  'gross_receipts', 'returns', 'cogs', 'other_income', 'guaranteed_payments', 'repairs', 'bad_debts',
  'rent', 'taxes_licenses', 'interest', 'depreciation', 'other_deductions',
];
const INCOME_TAX_LINES = new Set<TaxLine>(['gross_receipts', 'other_income']);
const DEDUCTION_TAX_LINES = new Set<TaxLine>(['guaranteed_payments', 'repairs', 'bad_debts', 'rent', 'taxes_licenses', 'interest', 'depreciation', 'other_deductions']);

/** Split a total by percentage: every member but the last is rounded; the last takes the remainder (Laura, in MEMBERS order). */
export function allocate(total_cents: number, shares: Record<Member, number>): Record<Member, number> {
  const out = {} as Record<Member, number>;
  let given = 0;
  MEMBERS.forEach((m, i) => {
    if (i === MEMBERS.length - 1) { out[m] = total_cents - given; return; }
    const part = Math.round(total_cents * num(shares[m]) / 100);
    out[m] = part;
    given += part;
  });
  return out;
}

/**
 * Page 1 by tax line (2025 labels), Schedule K, a K-1 per member and
 * Schedule L from the begin and end balance sheets. Accounts with no tax line
 * are balance sheet accounts and stay off page 1; income or expense accounts
 * that lack one are listed under `untagged` so they can be fixed.
 */
export function build1065(
  tbYear: TrialBalanceRow[],
  capital: MemberCapital,
  shares: Record<Member, number>,
  bsBegin: BalanceSheet | null = null,
  bsEnd: BalanceSheet | null = null,
): Form1065 {
  const amounts: Record<TaxLine, number> = Object.fromEntries(TAX_LINE_ORDER.map((t) => [t, 0])) as Record<TaxLine, number>;
  const untagged: SectionRow[] = [];
  for (const r of sortRows(tbYear)) {
    if (r.type !== 'income' && r.type !== 'cogs' && r.type !== 'expense') continue;
    const net = debitNet(r, false);
    if (!r.tax_line) {
      if (net !== 0) untagged.push({ account_id: r.account_id, code: r.code, name: r.name, subtype: r.subtype, amount_cents: r.type === 'income' ? -net : net });
      continue;
    }
    amounts[r.tax_line] += INCOME_TAX_LINES.has(r.tax_line) ? -net : net;
  }
  const lines = TAX_LINE_ORDER.map((t) => ({ tax_line: t, label: TAX_LINE_LABELS[t], amount_cents: amounts[t] }));
  const total_income_cents = amounts.gross_receipts - amounts.returns - amounts.cogs + amounts.other_income;
  const total_deductions_cents = TAX_LINE_ORDER.filter((t) => DEDUCTION_TAX_LINES.has(t)).reduce((s, t) => s + amounts[t], 0);
  const ordinary_income_cents = total_income_cents - total_deductions_cents;

  const incomeShare = allocate(ordinary_income_cents, shares);
  const k1 = {} as Form1065['k1'];
  let distributions_cents = 0;
  for (const m of MEMBERS) {
    const cap = capital.members[m];
    const dist = num(cap?.distributions_cents);
    distributions_cents += dist;
    k1[m] = { pct: num(shares[m]), ordinary_income_cents: incomeShare[m], distributions_cents: dist, capital: cap };
  }
  return {
    page1: { lines, total_income_cents, total_deductions_cents, ordinary_income_cents },
    schedule_k: { ordinary_income_cents, distributions_cents },
    k1,
    schedule_l: { begin: summarizeBalanceSheet(bsBegin), end: summarizeBalanceSheet(bsEnd) },
    untagged,
  };
}

// ── general ledger helpers ───────────────────────────────────────────────────

/** Running balance for one account's lines, signed by its normal side. */
export function runningBalances<T extends { debit_cents: number; credit_cents: number }>(lines: T[], normal: 'debit' | 'credit', opening = 0): number[] {
  let bal = opening;
  return lines.map((l) => {
    const net = num(l.debit_cents) - num(l.credit_cents);
    bal += normal === 'credit' ? -net : net;
    return bal;
  });
}

// ── dates ────────────────────────────────────────────────────────────────────

/** ISO date ± n days, computed in UTC so there is no daylight-saving drift. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d) + n * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** The period of the same length that ends the day before `from`. */
export function priorPeriod(from: string, to: string): { from: string; to: string } {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const days = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
  const pTo = addDays(from, -1);
  return { from: addDays(pTo, -days), to: pTo };
}
